// Per-material visual style table. The renderer pre-compiles a closure
// for each material id at startup; the hot loop just dispatches by id and
// fills RGBA bytes. All styles are deterministic functions of:
//   (cellSeed, frameIndex, neighbourSeed, materialPalette).
//
// Adding a new style is two steps:
//   1. Extend `RenderStyleKind` in sim/types.ts.
//   2. Add a builder below and wire it into `buildStyleTable`.

import type { MaterialDef, RenderStyle, RenderStyleKind } from "../sim/types";

/** Per-cell color writer. Writes RGBA into `out` at offset `i * 4`. */
export type Shader = (
  out: Uint8Array,
  i: number,
  seed: number,
  frame: number,
) => void;

/** Built once per material list change. Indexed by material id. */
export type StyleTable = Shader[];

const EMPTY_RGBA: Shader = (out, i) => {
  out[i] = 0;
  out[i + 1] = 0;
  out[i + 2] = 0;
  out[i + 3] = 0;
};

function clamp(n: number): number {
  return n < 0 ? 0 : n > 255 ? 255 : n | 0;
}

function clampUnit(n: number): number {
  return n < 0 ? 0 : n > 1 ? 1 : n;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function lerpColor(
  a: readonly [number, number, number],
  b: readonly [number, number, number],
  t: number,
): [number, number, number] {
  return [
    clamp(lerp(a[0], b[0], t)),
    clamp(lerp(a[1], b[1], t)),
    clamp(lerp(a[2], b[2], t)),
  ];
}

/**
 * Cheap deterministic hash of (seed, frame). Returns a value in [0, 1).
 * Good enough for visual jitter; do not use this for anything that
 * needs to match the simulation's seeded RNG.
 */
function hash01(seed: number, frame: number): number {
  let x = (seed * 374761393 + frame * 668265263) | 0;
  x = (x ^ (x >>> 13)) | 0;
  x = Math.imul(x, 1274126177) | 0;
  x = (x ^ (x >>> 16)) >>> 0;
  return x / 0xffffffff;
}

function paletteFromColor(
  color: readonly [number, number, number],
): [number, number, number][] {
  const [r, g, b] = color;
  return [
    [clamp(r * 0.7), clamp(g * 0.7), clamp(b * 0.7)],
    [r, g, b],
    [clamp(r + (255 - r) * 0.4), clamp(g + (255 - g) * 0.4), clamp(b + (255 - b) * 0.4)],
  ];
}

function defaultSpeedFor(kind: RenderStyleKind): number {
  switch (kind) {
    case "flame":
      return 6;
    case "lightning":
      return 16;
    case "liquid":
      return 3;
    case "glow":
      return 2;
    case "sparkle":
      return 4;
    case "noise":
      return 1;
    case "metallic":
      return 1;
    case "smoke":
      // Slow drift through the palette: a full dark→pale sweep takes
      // ~24 frames, slow enough to look turbulent rather than strobing.
      return 12;
    default:
      return 1;
  }
}

interface BuiltStyle {
  kind: RenderStyleKind;
  palette: [number, number, number][];
  speed: number;
  amount: number;
  base: [number, number, number];
}

function resolveStyle(material: MaterialDef): BuiltStyle | null {
  const base = material.color ?? [180, 180, 180];
  const render: RenderStyle = material.render ?? {};
  const kind: RenderStyleKind = render.style ?? "flat";
  if (kind === "flat") return null;
  const palette =
    render.palette && render.palette.length > 0
      ? render.palette
      : paletteFromColor(base);
  return {
    kind,
    palette,
    speed: render.speed ?? defaultSpeedFor(kind),
    amount: render.amount ?? 0.4,
    base,
  };
}

function buildShader(material: MaterialDef): Shader {
  const baseColor = material.color ?? [180, 180, 180];
  const built = resolveStyle(material);

  if (!built) {
    // Flat: just write the constant color.
    const [r, g, b] = baseColor;
    return (out, i) => {
      out[i] = r;
      out[i + 1] = g;
      out[i + 2] = b;
      out[i + 3] = 255;
    };
  }

  const { kind, palette, speed, amount } = built;

  switch (kind) {
    case "flame": {
      // Per-cell baseline + small oscillation. The seed picks where in
      // the palette the cell normally sits (some cells are persistently
      // dim embers, others bright tongues), and a slow sine adds a small
      // flicker around that baseline. Adjacent cells with different seeds
      // therefore land at very different palette stops most frames, which
      // gives fire its broken-up internal structure rather than a single
      // synchronised wash of colour.
      const last = palette.length - 1;
      const phaseStep = 1 / Math.max(1, speed);
      return (out, i, seed, frame) => {
        // Baseline in [0.2, 1.0] so even the dimmest cell isn't pure
        // black; high-seed cells sit closer to the white-hot tip.
        const baseline = 0.2 + (seed / 255) * 0.8;
        // Sine oscillation amplitude 0.35; cell-specific phase from seed.
        const flicker =
          0.35 * Math.sin(frame * phaseStep * 1.3 + seed * 0.12)
          + 0.18 * (hash01(seed, (frame >> 1) | 0) - 0.5);
        const stop = clampUnit(baseline + flicker);
        const idx = stop * last;
        const lo = Math.floor(idx);
        const hi = Math.min(lo + 1, last);
        const t = idx - lo;
        const c = lerpColor(palette[lo], palette[hi], t);
        out[i] = c[0];
        out[i + 1] = c[1];
        out[i + 2] = c[2];
        out[i + 3] = 255;
      };
    }

    case "liquid": {
      // Slow shimmer between two palette colors plus a horizontal wave.
      const [a, b] = palette.length >= 2 ? [palette[0], palette[palette.length - 1]] : [palette[0], palette[0]];
      return (out, i, seed, frame) => {
        const wave = 0.5 + 0.5 * Math.sin((frame / speed) + seed * 0.3);
        const c = lerpColor(a, b, wave * amount + (1 - amount) * 0.5);
        out[i] = c[0];
        out[i + 1] = c[1];
        out[i + 2] = c[2];
        out[i + 3] = 255;
      };
    }

    case "lightning": {
      // High-frequency bright flash, biased toward white.
      const [a, b] =
        palette.length >= 2
          ? [palette[0], palette[palette.length - 1]]
          : [palette[0], [255, 255, 255] as [number, number, number]];
      return (out, i, seed, frame) => {
        const flash = hash01(seed ^ 0x5a, frame * speed) > 0.4 ? 1 : 0.3;
        const c = lerpColor(a, b, flash);
        out[i] = c[0];
        out[i + 1] = c[1];
        out[i + 2] = c[2];
        out[i + 3] = 255;
      };
    }

    case "glow": {
      // Smooth pulse between palette ends.
      const [a, b] =
        palette.length >= 2 ? [palette[0], palette[palette.length - 1]] : [palette[0], palette[0]];
      return (out, i, seed, frame) => {
        const pulse = 0.5 + 0.5 * Math.sin((frame / speed) * 0.5 + seed * 0.05);
        const c = lerpColor(a, b, pulse);
        out[i] = c[0];
        out[i + 1] = c[1];
        out[i + 2] = c[2];
        out[i + 3] = 255;
      };
    }

    case "sparkle": {
      // Mostly a constant color; occasional bright sparkles.
      const base = palette[Math.floor(palette.length / 2)] ?? baseColor;
      const top = palette[palette.length - 1] ?? base;
      return (out, i, seed, frame) => {
        const trigger = hash01(seed, Math.floor(frame / speed));
        if (trigger > 1 - amount * 0.1) {
          out[i] = top[0];
          out[i + 1] = top[1];
          out[i + 2] = top[2];
        } else {
          out[i] = base[0];
          out[i + 1] = base[1];
          out[i + 2] = base[2];
        }
        out[i + 3] = 255;
      };
    }

    case "noise": {
      // Per-cell jitter on top of the base color (sand-grain / wood-grain
      // feel). The `amount` parameter is the RGB amplitude in 0..255 units;
      // the jitter is symmetric around zero so the average colour stays
      // the base. When a palette is supplied each cell picks a palette
      // stop by seed before the jitter is applied, which gives multi-tone
      // grain (e.g. wood with a few brown shades) instead of a single hue.
      const stops: ReadonlyArray<[number, number, number]> =
        palette.length > 1 ? palette : [baseColor];
      const stopCount = stops.length;
      return (out, i, seed) => {
        const stop = stops[seed % stopCount] ?? baseColor;
        // (seed/255)*2 - 1 maps the seed byte into [-1, 1].
        const t = (seed / 255) * 2 - 1;
        const j = t * amount;
        out[i] = clamp(stop[0] + j);
        out[i + 1] = clamp(stop[1] + j);
        out[i + 2] = clamp(stop[2] + j);
        out[i + 3] = 255;
      };
    }

    case "smoke": {
      // Soft, wispy gas: each cell picks a palette stop from a slow drift
      // through (seed, frame) space and adds a per-frame shimmer on top.
      // Adjacent cells land at different stops and animate out of phase,
      // so a smoke plume reads as turbulent grey rather than a uniform
      // block of dark pixels. Inspired by sandspiel's gas shader, which
      // varies lightness with per-pixel noise and saturation with
      // density (see references/sandspiel/js/glsl/sand.glsl `type == 4`).
      const stops: ReadonlyArray<[number, number, number]> =
        palette.length > 1 ? palette : [baseColor];
      const last = stops.length - 1;
      const drift = 1 / Math.max(1, speed);
      const jitterAmp = amount;
      return (out, i, seed, frame) => {
        // Stop selection drifts with frame so each cell slowly slides
        // along the dark→pale axis. Different seeds get different
        // phases (seed * 0.07) so neighbours desynchronise.
        const phase = frame * drift * 0.5 + seed * 0.07;
        const wave = 0.5 + 0.5 * Math.sin(phase);
        const idxF = wave * last;
        const lo = Math.floor(idxF);
        const hi = Math.min(lo + 1, last);
        const t = idxF - lo;
        const c = lerpColor(stops[lo], stops[hi], t);
        // High-frequency seeded jitter for grain. hash01 gives a stable
        // per-(seed, frame-bucket) value; the bucket size keeps the
        // shimmer sub-frame instead of strobing.
        const noise = (hash01(seed, (frame >> 1) | 0) - 0.5) * jitterAmp;
        out[i] = clamp(c[0] + noise);
        out[i + 1] = clamp(c[1] + noise);
        out[i + 2] = clamp(c[2] + noise);
        out[i + 3] = 255;
      };
    }

    case "metallic": {
      // Brightness gradient driven by seed (gives a brushed look).
      const [r, g, b] = baseColor;
      return (out, i, seed) => {
        const k = (seed / 255) * 0.5 + 0.75;
        out[i] = clamp(r * k);
        out[i + 1] = clamp(g * k);
        out[i + 2] = clamp(b * k);
        out[i + 3] = 255;
      };
    }

    default: {
      const [r, g, b] = baseColor;
      return (out, i) => {
        out[i] = r;
        out[i + 1] = g;
        out[i + 2] = b;
        out[i + 3] = 255;
      };
    }
  }
}

/** Build the per-material shader table once. */
export function buildStyleTable(materials: MaterialDef[]): StyleTable {
  return materials.map((m, idx) => (idx === 0 ? EMPTY_RGBA : buildShader(m)));
}
